import { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, orderBy, query } from "firebase/firestore";
import { AlertCircle, Inbox, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { db } from "@/firebase/config";

export default function FormSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [orderClicks, setOrderClicks] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [submissionSnap, clickSnap] = await Promise.all([
          getDocs(query(collection(db, "formSubmissions"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "orderClicks"), orderBy("clickedAt", "desc"))),
        ]);
        setSubmissions(submissionSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setOrderClicks(clickSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Failed to fetch form submissions:", err);
        setError("Could not load form submissions.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedCount = selectedIds.length;
  const allSelected = submissions.length > 0 && selectedCount === submissions.length;

  const toggleSelected = (id) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : submissions.map((item) => item.id));
  };

  const deleteSelected = async () => {
    if (selectedCount === 0) {
      toast.info("Select a response first.");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedCount} selected response${selectedCount === 1 ? "" : "s"}? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeleting(true);
    const idsToDelete = [...selectedIds];
    try {
      await Promise.all(idsToDelete.map((id) => deleteDoc(doc(db, "formSubmissions", id))));
      setSubmissions((current) => current.filter((item) => !idsToDelete.includes(item.id)));
      setSelectedIds([]);
      toast.success(`Deleted ${idsToDelete.length} response${idsToDelete.length === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error("Failed to delete selected submissions:", err);
      toast.error("Could not delete selected response. Check admin permissions and try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-form-submissions">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-pixel text-[10px] mb-1">// ADMIN - SUBMISSIONS</p>
          <h2 className="font-heading text-3xl uppercase">Form Submissions</h2>
        </div>
        <button
          type="button"
          onClick={deleteSelected}
          disabled={deleting || selectedCount === 0}
          className="retro-btn bg-retro-pink px-4 py-3 inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="delete-selected-submissions"
        >
          {deleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
          Delete selected{selectedCount ? ` (${selectedCount})` : ""}
        </button>
      </div>

      {loading && (
        <div className="retro-card p-8 flex items-center gap-3">
          <Loader2 className="animate-spin" />
          <span className="font-pixel text-[10px]">LOADING SUBMISSIONS...</span>
        </div>
      )}

      {!loading && error && (
        <div className="retro-card p-8 flex items-center gap-3 bg-retro-pink">
          <AlertCircle />
          <p className="font-body text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && submissions.length === 0 && (
        <div className="retro-card p-8 text-center">
          <Inbox className="mx-auto mb-3 text-retro-purple" size={36} />
          <p className="font-heading text-xl uppercase">No submissions yet</p>
          <p className="font-body text-sm text-retro-ink/70 mt-1">New public order form entries will appear here.</p>
        </div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <div className="retro-card p-0 overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left font-body text-sm">
              <thead className="bg-retro-yellow border-b-[3px] border-retro-ink">
                <tr>
                  <Th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all submissions"
                      className="h-4 w-4 accent-retro-purple"
                      data-testid="select-all-submissions"
                    />
                  </Th>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Order Readiness</Th>
                  <Th>Which College</Th>
                  <Th>Coloured / Black & White</Th>
                  <Th>Coupon</Th>
                  <Th>Message</Th>
                  <Th>Submitted</Th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((item) => (
                  <tr key={item.id} className="border-b border-retro-ink/15 align-top">
                    <Td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelected(item.id)}
                        aria-label={`Select response from ${item.name || item.email || "unknown"}`}
                        className="h-4 w-4 accent-retro-purple"
                        data-testid={`select-submission-${item.id}`}
                      />
                    </Td>
                    <Td>{item.name || "-"}</Td>
                    <Td>{item.email || "-"}</Td>
                    <Td>{item.interestLevel || item.phone || "-"}</Td>
                    <Td>{item.college || "-"}</Td>
                    <Td>{item.plannerPreference || item.interestType || item.preferredTime || "-"}</Td>
                    <Td>{item.couponCode || "-"}</Td>
                    <Td className="min-w-64">{item.message || "-"}</Td>
                    <Td>{formatDate(item.createdAt)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="retro-card p-0 overflow-hidden bg-white">
          <div className="border-b-[3px] border-retro-ink bg-retro-mint px-5 py-3">
            <h3 className="font-heading text-xl uppercase">Order Now Clicks</h3>
            <p className="font-body text-xs text-retro-ink/70 mt-1">
              Tracks button clicks before redirecting to payment.
            </p>
          </div>
          {orderClicks.length === 0 ? (
            <div className="p-6 font-body text-sm text-retro-ink/70">No Order Now clicks tracked yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left font-body text-sm">
                <thead className="bg-retro-yellow border-b-[3px] border-retro-ink">
                  <tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Source</Th>
                    <Th>Preference</Th>
                    <Th>Coupon</Th>
                    <Th>Payment URL</Th>
                    <Th>Clicked</Th>
                  </tr>
                </thead>
                <tbody>
                  {orderClicks.map((item) => (
                    <tr key={item.id} className="border-b border-retro-ink/15 align-top">
                      <Td>{item.leadName || item.lead?.name || "-"}</Td>
                      <Td>{item.leadEmail || item.lead?.email || "-"}</Td>
                      <Td>{item.source || "-"}</Td>
                      <Td>{item.plannerPreference || item.lead?.plannerPreference || "-"}</Td>
                      <Td>{item.couponCode || item.lead?.couponCode || "-"}</Td>
                      <Td className="max-w-72 break-all">{item.paymentUrl || "-"}</Td>
                      <Td>{formatDate(item.clickedAt)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 font-pixel text-[9px] uppercase whitespace-nowrap">{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}

function formatDate(value) {
  if (!value) return "-";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
